import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useAdminTranslation } from '../../utils/useAdminTranslation';
import { translateCategory, translateCountry } from '../../utils/translations';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import {
  Search,
  Mail,
  X,
  Globe,
  FolderOpen,
  ChevronDown,
  Trash2,
  Download,
  Upload,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  Calendar,
  Check,
  Loader2,
  Copy,
  AlertTriangle,
  Plus,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductsTab = ({ onProductEdit }) => {
  const { language } = useLanguage();
  const { tx } = useAdminTranslation();
  
  // Products state
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [productSearch, setProductSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [adminFilters, setAdminFilters] = useState({
    countries: [],
    categories: [],
    siteTypes: [],
    drMin: '',
    drMax: '',
    trafficMin: '',
    trafficMax: '',
    priceMin: '',
    priceMax: '',
    email: ''
  });
  const [filterOptions, setFilterOptions] = useState({ countries: [], categories: [], siteTypes: [] });
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [exportingSelected, setExportingSelected] = useState(false);
  const [changeDays, setChangeDays] = useState(1);
  const [exportingChanges, setExportingChanges] = useState(false);
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [replaceAll, setReplaceAll] = useState(false);
  
  // Add product state
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [addingProduct, setAddingProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    website: '', country: '', category: '', pvp: 0, pc: 0,
    premium_purchase_price: 0, premium_price: 0,
    web_email: '', second_email: '', dr: 0, organic_traffic: 0,
    language: '', site_type: '', link_type: '', link_duration: '',
    tag_label: '', mas_info_pc: '',
    allow_purchase: true, allow_redaction: false, allow_by_admin: true,
    casino: false, cbd: false, gambling: false, cryptocurrency: false,
    sex: false, erotic: false, vpn: false, tarot: false, cannabis: false,
  });
  
  // Delete all dialog
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Texts for current language
  const texts = {
    es: {
      products: 'Productos',
      searchPlaceholder: 'Buscar por dominio...',
      search: 'Buscar',
      clearSearch: 'Limpiar',
      allCountries: 'Todos los países',
      allCategories: 'Todas las categorías',
      country: 'País',
      category: 'Categoría',
      drMin: 'DR mín',
      drMax: 'DR máx',
      trafficMin: 'Tráfico mín',
      trafficMax: 'Tráfico máx',
      priceMin: 'Precio mín',
      priceMax: 'Precio máx',
      selectAll: 'Seleccionar todo',
      deselectAll: 'Deseleccionar',
      selected: 'seleccionados',
      exportSelected: 'Descargar Selección',
      exportChanges: 'Descargar Cambios',
      exportingChanges: 'Descargando...',
      days: 'días',
      today: 'Hoy',
      lastDays: 'Últimos',
      page: 'Página',
      of: 'de',
      showing: 'Mostrando',
      noProducts: 'No hay productos',
      website: 'Sitio Web',
      dr: 'DR',
      da: 'DA',
      traffic: 'Tráfico',
      pvp: 'PVP',
      pc: 'PC',
      pvpSensible: 'PVP Sens.',
      pcSensible: 'PC Sens.',
      email: 'Email',
      email2: '2º Email',
      tagLabel: 'Tag',
      masInfo: 'Más Info',
      linkType: 'Tipo Enlace',
      actions: 'Acciones',
      edit: 'Editar',
      delete: 'Eliminar',
      confirmDelete: '¿Estás seguro de que quieres eliminar este producto?',
      confirmDeleteAllTitle: '¿Eliminar TODOS los productos?',
      confirmDeleteAllDesc: 'Esta acción no se puede deshacer. Todos los productos serán eliminados de la base de datos.',
      typeToConfirm: 'Escribe ELIMINAR para confirmar:',
      deleteWord: 'ELIMINAR',
      importTitle: 'Importador de Productos',
      importDescription: 'Sube un archivo Excel (.xlsx) con los productos a importar',
      selectFile: 'Seleccionar Archivo',
      replaceAll: 'Reemplazar todos los productos existentes',
      replaceAllWarning: 'Esta opción eliminará todos los productos actuales antes de importar',
      downloadTemplate: 'Descargar Plantilla',
      importButton: 'Importar',
      importing: 'Importando...',
      deleteAll: 'Eliminar Todo',
    },
    en: {
      products: 'Products',
      searchPlaceholder: 'Search by domain...',
      search: 'Search',
      clearSearch: 'Clear',
      allCountries: 'All countries',
      allCategories: 'All categories',
      country: 'Country',
      category: 'Category',
      drMin: 'DR min',
      drMax: 'DR max',
      trafficMin: 'Traffic min',
      trafficMax: 'Traffic max',
      priceMin: 'Price min',
      priceMax: 'Price max',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      selected: 'selected',
      exportSelected: 'Download Selected',
      exportChanges: 'Download Changes',
      exportingChanges: 'Downloading...',
      days: 'days',
      today: 'Today',
      lastDays: 'Last',
      page: 'Page',
      of: 'of',
      showing: 'Showing',
      noProducts: 'No products',
      website: 'Website',
      dr: 'DR',
      da: 'DA',
      traffic: 'Traffic',
      pvp: 'PVP',
      pc: 'PC',
      pvpSensible: 'PVP Sens.',
      pcSensible: 'PC Sens.',
      email: 'Email',
      email2: '2nd Email',
      tagLabel: 'Tag',
      masInfo: 'More Info',
      linkType: 'Link Type',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this product?',
      confirmDeleteAllTitle: 'Delete ALL products?',
      confirmDeleteAllDesc: 'This action cannot be undone. All products will be deleted from the database.',
      typeToConfirm: 'Type DELETE to confirm:',
      deleteWord: 'DELETE',
      importTitle: 'Product Importer',
      importDescription: 'Upload an Excel file (.xlsx) with the products to import',
      selectFile: 'Select File',
      replaceAll: 'Replace all existing products',
      replaceAllWarning: 'This option will delete all current products before importing',
      downloadTemplate: 'Download Template',
      importButton: 'Import',
      importing: 'Importing...',
      deleteAll: 'Delete All',
    }
  };
  
  const t = texts[language] || texts.es;

  // Fetch products
  const fetchProducts = useCallback(async (page = 1, search = productSearch, filters = adminFilters, sort = sortField, order = sortOrder, limit = null) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit || itemsPerPage);
      if (search) params.append('search', search);
      if (filters.countries?.length > 0) params.append('countries', filters.countries.join(','));
      if (filters.categories?.length > 0) params.append('categories', filters.categories.join(','));
      if (filters.siteTypes?.length > 0) params.append('site_types', filters.siteTypes.join(','));
      if (filters.drMin) params.append('dr_min', filters.drMin);
      if (filters.drMax) params.append('dr_max', filters.drMax);
      if (filters.trafficMin) params.append('traffic_min', filters.trafficMin);
      if (filters.trafficMax) params.append('traffic_max', filters.trafficMax);
      if (filters.priceMin) params.append('price_min', filters.priceMin);
      if (filters.priceMax) params.append('price_max', filters.priceMax);
      if (filters.email) params.append('email', filters.email);
      if (sort) {
        params.append('sort_by', sort);
        params.append('sort_order', order);
      }
      
      const response = await axios.get(`${API}/admin/products?${params.toString()}`);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
      setSelectedProducts(new Set());
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(tx('Error al cargar productos', 'Error loading products'));
    }
  }, [productSearch, adminFilters, sortField, sortOrder, itemsPerPage, tx]);

  // Handle items per page change
  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    fetchProducts(1, productSearch, adminFilters, sortField, sortOrder, newLimit);
  };

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/filters/options`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchProducts(1);
    fetchFilterOptions();
  }, []);

  // Handlers
  const handleProductSearch = (e) => {
    e.preventDefault();
    fetchProducts(1, productSearch, adminFilters);
  };

  const handleClearSearch = () => {
    setProductSearch('');
    const emptyFilters = { countries: [], categories: [], drMin: '', drMax: '', trafficMin: '', trafficMax: '', priceMin: '', priceMax: '', email: '' };
    setAdminFilters(emptyFilters);
    fetchProducts(1, '', emptyFilters);
  };

  const handleAdminFilterChange = (key, value) => {
    const newFilters = { ...adminFilters, [key]: value };
    setAdminFilters(newFilters);
    fetchProducts(1, productSearch, newFilters);
  };

  const handleCountryToggle = (country) => {
    const currentCountries = adminFilters.countries || [];
    const newCountries = currentCountries.includes(country)
      ? currentCountries.filter(c => c !== country)
      : [...currentCountries, country];
    handleAdminFilterChange('countries', newCountries);
  };

  const handleCategoryToggle = (category) => {
    const currentCategories = adminFilters.categories || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    handleAdminFilterChange('categories', newCategories);
  };

  const handleSort = (field) => {
    let newOrder = 'asc';
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortOrder(newOrder);
    fetchProducts(1, productSearch, adminFilters, field, newOrder);
  };

  const handleSelectProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const handleSelectAllFromDB = async () => {
    try {
      const params = new URLSearchParams();
      if (productSearch) params.append('search', productSearch);
      if (adminFilters.countries?.length > 0) params.append('countries', adminFilters.countries.join(','));
      if (adminFilters.categories?.length > 0) params.append('categories', adminFilters.categories.join(','));
      if (adminFilters.drMin) params.append('dr_min', adminFilters.drMin);
      if (adminFilters.drMax) params.append('dr_max', adminFilters.drMax);
      if (adminFilters.trafficMin) params.append('traffic_min', adminFilters.trafficMin);
      if (adminFilters.trafficMax) params.append('traffic_max', adminFilters.trafficMax);
      if (adminFilters.priceMin) params.append('price_min', adminFilters.priceMin);
      if (adminFilters.priceMax) params.append('price_max', adminFilters.priceMax);
      if (adminFilters.email) params.append('email', adminFilters.email);

      const response = await axios.get(`${API}/admin/products/ids?${params.toString()}`);
      setSelectedProducts(new Set(response.data.ids));
      toast.success(tx(`${response.data.ids.length} productos seleccionados`, `${response.data.ids.length} products selected`));
    } catch (error) {
      console.error('Error selecting all from DB:', error);
      toast.error(tx('Error al seleccionar productos', 'Error selecting products'));
    }
  };

  const handleExportSelected = async () => {
    if (selectedProducts.size === 0) {
      toast.error(tx('No hay productos seleccionados', 'No products selected'));
      return;
    }
    
    setExportingSelected(true);
    try {
      const response = await axios.post(`${API}/admin/products/export-selected`, 
        { product_ids: Array.from(selectedProducts) },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `productos_seleccionados_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(tx(`${selectedProducts.size} productos exportados`, `${selectedProducts.size} products exported`));
    } catch (error) {
      console.error('Error exporting selected:', error);
      toast.error(tx('Error al exportar', 'Error exporting'));
    } finally {
      setExportingSelected(false);
    }
  };

  const handleExportChanges = async () => {
    setExportingChanges(true);
    try {
      const response = await axios.get(`${API}/admin/products/export-changes?days=${changeDays}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cambios_productos_${changeDays}d_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(tx('Cambios exportados', 'Changes exported'));
    } catch (error) {
      console.error('Error exporting changes:', error);
      toast.error(tx('Error al exportar cambios', 'Error exporting changes'));
    } finally {
      setExportingChanges(false);
    }
  };

  const handleProductDelete = async (productId) => {
    if (!window.confirm(t.confirmDelete)) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success(tx('Producto eliminado', 'Product deleted'));
      fetchProducts(pagination.page, productSearch, adminFilters);
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(tx('Error al eliminar producto', 'Error deleting product'));
    }
  };

  const handleDeleteAll = async () => {
    if (deleteConfirmText !== t.deleteWord) {
      toast.error(tx('Texto de confirmación incorrecto', 'Incorrect confirmation text'));
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/products/all`);
      toast.success(tx('Todos los productos eliminados', 'All products deleted'));
      setDeleteAllDialogOpen(false);
      setDeleteConfirmText('');
      fetchProducts(1);
    } catch (error) {
      console.error('Error deleting all products:', error);
      toast.error(tx('Error al eliminar productos', 'Error deleting products'));
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error(tx('Selecciona un archivo', 'Select a file'));
      return;
    }
    
    setImporting(true);
    setImportProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('replace_all', replaceAll);
      
      const response = await axios.post(`${API}/admin/products/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setImportProgress(progress);
        }
      });
      
      toast.success(tx(
        `${response.data.imported} productos importados`,
        `${response.data.imported} products imported`
      ));
      
      setImportFile(null);
      setReplaceAll(false);
      fetchProducts(1);
    } catch (error) {
      console.error('Error importing products:', error);
      toast.error(error.response?.data?.detail || tx('Error al importar', 'Error importing'));
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/products/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(tx('Error al descargar plantilla', 'Error downloading template'));
    }
  };

  const openProductDialog = (product) => {
    if (onProductEdit) {
      onProductEdit(product);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.website.trim()) {
      toast.error(language === 'es' ? 'El dominio es obligatorio' : 'Website is required');
      return;
    }
    setAddingProduct(true);
    try {
      await axios.post(`${API}/admin/products`, newProduct);
      toast.success(language === 'es' ? 'Producto creado' : 'Product created');
      setShowAddProduct(false);
      setNewProduct({
        website: '', country: '', category: '', pvp: 0, pc: 0,
        premium_purchase_price: 0, premium_price: 0,
        web_email: '', second_email: '', dr: 0, organic_traffic: 0,
        language: '', site_type: '', link_type: '', link_duration: '',
        tag_label: '', mas_info_pc: '',
        allow_purchase: true, allow_redaction: false, allow_by_admin: true,
        casino: false, cbd: false, gambling: false, cryptocurrency: false,
        sex: false, erotic: false, vpn: false, tarot: false, cannabis: false,
      });
      fetchProducts(1, productSearch, adminFilters);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear producto');
    } finally {
      setAddingProduct(false);
    }
  };

  return (
    <div data-testid="products-tab">
      {/* Search Bar */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <form onSubmit={handleProductSearch} className="flex flex-col gap-3">
            {/* Domain and Email Search */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={productSearch}
                  onChange={(e) => {
                    let value = e.target.value
                      .replace(/^https?:\/\//i, '')
                      .replace(/^www\./i, '')
                      .replace(/\/+$/, '')
                      .trim();
                    setProductSearch(value);
                    
                    if (searchTimeout) clearTimeout(searchTimeout);
                    const timeout = setTimeout(() => {
                      fetchProducts(1, value, adminFilters);
                    }, 500);
                    setSearchTimeout(timeout);
                  }}
                  className="pl-10"
                  data-testid="product-search-input"
                />
              </div>
              
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={tx('Buscar por email...', 'Search by email...')}
                  value={adminFilters.email || ''}
                  onChange={(e) => handleAdminFilterChange('email', e.target.value)}
                  className="pl-10"
                  data-testid="product-email-filter"
                />
                {adminFilters.email && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => handleAdminFilterChange('email', '')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" data-testid="search-products-btn">
                  <Search className="h-4 w-4 mr-2" />
                  {t.search}
                </Button>
                <Button type="button" onClick={() => setShowAddProduct(true)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-product-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {tx('Añadir', 'Add')}
                </Button>
                {(productSearch || adminFilters.countries?.length > 0 || adminFilters.categories?.length > 0 || adminFilters.email) && (
                  <Button type="button" variant="outline" onClick={handleClearSearch} data-testid="clear-search-btn">
                    <X className="h-4 w-4 mr-2" />
                    {t.clearSearch}
                  </Button>
                )}
              </div>
            </div>
          </form>
          
          {/* Filters Row */}
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t">
            {/* Country Multi-Select */}
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px] justify-between font-normal">
                    {adminFilters.countries?.length > 0 
                      ? `${adminFilters.countries.length} ${tx('países', 'countries')}`
                      : t.allCountries}
                    <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-2" align="start">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium">{t.country}</span>
                    {adminFilters.countries?.length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 text-xs"
                        onClick={() => handleAdminFilterChange('countries', [])}
                      >
                        {tx('Limpiar', 'Clear')}
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[250px]">
                    <div className="space-y-1">
                      {filterOptions.countries.map((country) => (
                        <label 
                          key={country} 
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                        >
                          <Checkbox
                            checked={adminFilters.countries?.includes(country)}
                            onCheckedChange={() => handleCountryToggle(country)}
                          />
                          <span className="text-sm">{translateCountry(country, language)}</span>
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Category Multi-Select */}
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[180px] justify-between font-normal">
                    {adminFilters.categories?.length > 0 
                      ? `${adminFilters.categories.length} ${tx('categorías', 'categories')}`
                      : t.allCategories}
                    <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-2" align="start">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium">{t.category}</span>
                    {adminFilters.categories?.length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 text-xs"
                        onClick={() => handleAdminFilterChange('categories', [])}
                      >
                        {tx('Limpiar', 'Clear')}
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[250px]">
                    <div className="space-y-1">
                      {filterOptions.categories.map((category) => (
                        <label 
                          key={category} 
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                        >
                          <Checkbox
                            checked={adminFilters.categories?.includes(category)}
                            onCheckedChange={() => handleCategoryToggle(category)}
                          />
                          <span className="text-sm">{translateCategory(category, language)}</span>
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Site Type Filter */}
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[160px] justify-between font-normal">
                    {adminFilters.siteTypes?.length > 0 
                      ? `${adminFilters.siteTypes.length} tipos`
                      : tx('Tipo Medio', 'Site Type')}
                    <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-2" align="start">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium">{tx('Tipo de Medio', 'Site Type')}</span>
                    {adminFilters.siteTypes?.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => handleAdminFilterChange('siteTypes', [])}
                      >
                        {tx('Limpiar', 'Clear')}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {['Periódico', 'Web/Blog', 'Blog', 'Web'].map((siteType) => (
                      <label
                        key={siteType}
                        className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                      >
                        <Checkbox
                          checked={adminFilters.siteTypes?.includes(siteType)}
                          onCheckedChange={() => {
                            const currentTypes = adminFilters.siteTypes || [];
                            const newTypes = currentTypes.includes(siteType)
                              ? currentTypes.filter(t => t !== siteType)
                              : [...currentTypes, siteType];
                            handleAdminFilterChange('siteTypes', newTypes);
                          }}
                        />
                        <span className="text-sm">{siteType}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Numeric Filters */}
            <Input
              type="number"
              placeholder={t.drMin}
              value={adminFilters.drMin}
              onChange={(e) => handleAdminFilterChange('drMin', e.target.value)}
              className="w-20"
            />
            <Input
              type="number"
              placeholder={t.drMax}
              value={adminFilters.drMax}
              onChange={(e) => handleAdminFilterChange('drMax', e.target.value)}
              className="w-20"
            />
            <Input
              type="number"
              placeholder={t.trafficMin}
              value={adminFilters.trafficMin}
              onChange={(e) => handleAdminFilterChange('trafficMin', e.target.value)}
              className="w-24"
            />
            <Input
              type="number"
              placeholder={t.trafficMax}
              value={adminFilters.trafficMax}
              onChange={(e) => handleAdminFilterChange('trafficMax', e.target.value)}
              className="w-24"
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-4">
        {/* Selection Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            data-testid="select-all-page-btn"
          >
            {selectedProducts.size === products.length ? t.deselectAll : t.selectAll}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAllFromDB}
            data-testid="select-all-db-btn"
          >
            {tx('Seleccionar todos (filtro)', 'Select all (filtered)')}
          </Button>
          {selectedProducts.size > 0 && (
            <Badge variant="secondary">
              {selectedProducts.size} {t.selected}
            </Badge>
          )}
        </div>
        
        {/* Export Actions */}
        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportSelected}
            disabled={selectedProducts.size === 0 || exportingSelected}
            data-testid="export-selected-btn"
          >
            {exportingSelected ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            {t.exportSelected}
          </Button>
          
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="1"
              max="365"
              value={changeDays}
              onChange={(e) => setChangeDays(parseInt(e.target.value) || 1)}
              className="w-16 h-9"
            />
            <span className="text-sm text-muted-foreground">{t.days}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportChanges}
              disabled={exportingChanges}
              data-testid="export-changes-btn"
            >
              {exportingChanges ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calendar className="h-4 w-4 mr-2" />}
              {exportingChanges ? t.exportingChanges : t.exportChanges}
            </Button>
          </div>
          
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteAllDialogOpen(true)}
            data-testid="delete-all-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t.deleteAll}
          </Button>
        </div>
      </div>
      
      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          <div className="border rounded-md overflow-auto" style={{ maxHeight: '600px' }}>
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                <TableRow className="text-left text-sm font-medium text-muted-foreground">
                  <TableHead className="w-10 p-2">
                    <Checkbox
                      checked={products.length > 0 && selectedProducts.size === products.length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="p-2 cursor-pointer min-w-[150px]" onClick={() => handleSort('website')}>
                    {t.website}
                    <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                  </TableHead>
                  <TableHead className="p-2 cursor-pointer text-right" onClick={() => handleSort('dr')}>
                    {t.dr}
                    <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                  </TableHead>
                  <TableHead className="p-2 cursor-pointer text-right" onClick={() => handleSort('traffic')}>
                    {t.traffic}
                    <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                  </TableHead>
                  <TableHead className="p-2 cursor-pointer text-right" onClick={() => handleSort('pvp')}>
                    {t.pvp}
                    <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                  </TableHead>
                  <TableHead className="p-2 text-right">{t.pc}</TableHead>
                  <TableHead className="p-2 text-right text-amber-600">{t.pvpSensible}</TableHead>
                  <TableHead className="p-2 text-right text-amber-600">{t.pcSensible}</TableHead>
                  <TableHead className="p-2">{t.email}</TableHead>
                  <TableHead className="p-2">{t.email2}</TableHead>
                  <TableHead className="p-2">{t.linkType}</TableHead>
                  <TableHead className="p-2">{t.tagLabel}</TableHead>
                  <TableHead className="p-2">{t.masInfo}</TableHead>
                  <TableHead className="p-2 text-right">{t.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-8 text-muted-foreground">
                      {t.noProducts}
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.id} className={selectedProducts.has(product.id) ? 'bg-muted/50' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={selectedProducts.has(product.id)}
                          onCheckedChange={() => handleSelectProduct(product.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <a 
                          href={`https://${product.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {product.website}
                        </a>
                      </TableCell>
                      <TableCell className="text-right">{product.dr || '-'}</TableCell>
                      <TableCell className="text-right">{(product.organic_traffic || product.traffic)?.toLocaleString() || '-'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {product.pvp > 0 && product.pc > 0 && product.pvp <= product.pc ? (
                          <span className="flex items-center justify-end gap-1">
                            <AlertTriangle className="h-3.5 w-3.5 text-red-500" title={language === 'es' ? 'PVP menor o igual que PC' : 'PVP less or equal than PC'} />
                            <span className="text-red-600">€{product.pvp?.toFixed(2)}</span>
                          </span>
                        ) : (
                          <span>€{product.pvp?.toFixed(2) || '0.00'}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">€{product.pc?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell className="text-right text-amber-600 font-medium">
                        {(() => {
                          const pp = product.premium_price || product.premium_sale_price || product.price_sensitive || 0;
                          const pcp = product.premium_purchase_price || product.precio_compra || 0;
                          if (pp > 0 && pcp > 0 && pp <= pcp) {
                            return (
                              <span className="flex items-center justify-end gap-1">
                                <AlertTriangle className="h-3.5 w-3.5 text-red-500" title={language === 'es' ? 'Precio venta sensible menor o igual que compra' : 'Sensitive sale price less or equal than purchase'} />
                                <span className="text-red-600">€{pp.toFixed(2)}</span>
                              </span>
                            );
                          }
                          return <span>€{pp.toFixed(2)}</span>;
                        })()}
                      </TableCell>
                      <TableCell className="text-right text-amber-600">
                        €{(product.premium_purchase_price || product.precio_compra || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="max-w-[150px]">
                        {product.web_email ? (
                          <span className="flex items-center gap-1 group cursor-pointer" title={product.web_email}
                            onClick={() => { navigator.clipboard.writeText(product.web_email); toast.success('Email copiado'); }}>
                            <span className="truncate">{product.web_email}</span>
                            <Copy className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 shrink-0" />
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="max-w-[100px]">
                        {product.second_email ? (
                          <span className="flex items-center gap-1 group cursor-pointer" title={product.second_email}
                            onClick={() => { navigator.clipboard.writeText(product.second_email); toast.success('Email copiado'); }}>
                            <span className="truncate">{product.second_email}</span>
                            <Copy className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 shrink-0" />
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="max-w-[100px] truncate" title={product.link_type}>{product.link_type || '-'}</TableCell>
                      <TableCell>
                        {product.tag_label && product.tag_label.toLowerCase() === 'yes' ? (
                          <Badge className="text-xs bg-red-100 text-red-700 border-red-200">Yes</Badge>
                        ) : (
                          <Badge className="text-xs bg-green-100 text-green-700 border-green-200">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate" title={product.mas_info_pc}>{product.mas_info_pc || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openProductDialog(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            {t.edit}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleProductDelete(product.id)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      
      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 flex-wrap gap-4">
        <span className="text-sm text-muted-foreground">
          {t.showing} {products.length} {t.of} {pagination.total}
        </span>
        <div className="flex items-center gap-4">
          {/* Items per page selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Mostrando</span>
            <select 
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="border border-border rounded-md px-2 py-1 text-sm bg-background"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={2000}>2000</option>
            </select>
            <span className="text-sm text-muted-foreground">por página</span>
          </div>
          
          {/* Page navigation */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => fetchProducts(pagination.page - 1, productSearch, adminFilters)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                {t.page} {pagination.page} {t.of} {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => fetchProducts(pagination.page + 1, productSearch, adminFilters)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Import Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {t.importTitle}
          </CardTitle>
          <CardDescription>{t.importDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="flex-1"
                data-testid="import-file-input"
              />
              <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {t.downloadTemplate}
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="replace-all"
                checked={replaceAll}
                onCheckedChange={(checked) => setReplaceAll(checked)}
              />
              <Label htmlFor="replace-all" className="text-sm">
                {t.replaceAll}
              </Label>
            </div>
            {replaceAll && (
              <p className="text-sm text-amber-600">{t.replaceAllWarning}</p>
            )}
            
            {importing && (
              <Progress value={importProgress} className="w-full" />
            )}
            
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
              className="w-fit"
              data-testid="import-products-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t.importing}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t.importButton}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Delete All Dialog */}
      <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              {t.confirmDeleteAllTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t.confirmDeleteAllDesc}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>{t.typeToConfirm}</Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
              placeholder={t.deleteWord}
              className="mt-2"
              data-testid="delete-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>
              {tx('Cancelar', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAll}
              disabled={deleteConfirmText !== t.deleteWord}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-all-btn"
            >
              {tx('Eliminar Todo', 'Delete All')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Product Dialog */}
      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{tx('Añadir Producto', 'Add Product')}</DialogTitle>
            <DialogDescription>{tx('Crear un nuevo medio manualmente', 'Create a new media manually')}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Website *</Label>
              <Input value={newProduct.website} onChange={(e) => setNewProduct({...newProduct, website: e.target.value})} placeholder="ejemplo.com" data-testid="add-product-website" />
            </div>
            <div>
              <Label>{tx('País', 'Country')}</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm" value={newProduct.country} onChange={(e) => setNewProduct({...newProduct, country: e.target.value})}>
                <option value="">{tx('Seleccionar', 'Select')}</option>
                {filterOptions.countries?.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>{tx('Categoría', 'Category')}</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm" value={newProduct.category} onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}>
                <option value="">{tx('Seleccionar', 'Select')}</option>
                {filterOptions.categories?.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>PVP (€)</Label>
              <Input type="number" step="0.01" value={newProduct.pvp} onChange={(e) => setNewProduct({...newProduct, pvp: parseFloat(e.target.value) || 0})} />
            </div>
            <div>
              <Label>PC (€)</Label>
              <Input type="number" step="0.01" value={newProduct.pc} onChange={(e) => setNewProduct({...newProduct, pc: parseFloat(e.target.value) || 0})} />
            </div>
            <div>
              <Label>{tx('PVP Sensible (€)', 'Sensitive PVP (€)')}</Label>
              <Input type="number" step="0.01" value={newProduct.premium_price} onChange={(e) => setNewProduct({...newProduct, premium_price: parseFloat(e.target.value) || 0})} />
            </div>
            <div>
              <Label>{tx('PC Sensible (€)', 'Sensitive PC (€)')}</Label>
              <Input type="number" step="0.01" value={newProduct.premium_purchase_price} onChange={(e) => setNewProduct({...newProduct, premium_purchase_price: parseFloat(e.target.value) || 0})} />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={newProduct.web_email} onChange={(e) => setNewProduct({...newProduct, web_email: e.target.value})} placeholder="contacto@medio.com" />
            </div>
            <div>
              <Label>2nd Email</Label>
              <Input value={newProduct.second_email} onChange={(e) => setNewProduct({...newProduct, second_email: e.target.value})} />
            </div>
            <div>
              <Label>DR</Label>
              <Input type="number" value={newProduct.dr} onChange={(e) => setNewProduct({...newProduct, dr: parseInt(e.target.value) || 0})} />
            </div>
            <div>
              <Label>{tx('Tráfico', 'Traffic')}</Label>
              <Input type="number" value={newProduct.organic_traffic} onChange={(e) => setNewProduct({...newProduct, organic_traffic: parseInt(e.target.value) || 0})} />
            </div>
            <div>
              <Label>{tx('Idioma', 'Language')}</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm" value={newProduct.language} onChange={(e) => setNewProduct({...newProduct, language: e.target.value})}>
                <option value="">{tx('Seleccionar', 'Select')}</option>
                {filterOptions.languages?.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label>{tx('Tipo Medio', 'Site Type')}</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm" value={newProduct.site_type} onChange={(e) => setNewProduct({...newProduct, site_type: e.target.value})}>
                <option value="">{tx('Seleccionar', 'Select')}</option>
                {filterOptions.siteTypes?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>{tx('Tipo Enlace', 'Link Type')}</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm" value={newProduct.link_type} onChange={(e) => setNewProduct({...newProduct, link_type: e.target.value})}>
                <option value="">{tx('Seleccionar', 'Select')}</option>
                {filterOptions.linkTypes?.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label>{tx('Duración', 'Duration')}</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm" value={newProduct.link_duration} onChange={(e) => setNewProduct({...newProduct, link_duration: e.target.value})}>
                <option value="">{tx('Seleccionar', 'Select')}</option>
                {filterOptions.linkDurations?.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label>Tag / Etiqueta</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm" value={newProduct.tag_label} onChange={(e) => setNewProduct({...newProduct, tag_label: e.target.value})}>
                <option value="">{tx('Seleccionar', 'Select')}</option>
                {filterOptions.tagLabels?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>{tx('Más info PC', 'More info PC')}</Label>
              <Input value={newProduct.mas_info_pc} onChange={(e) => setNewProduct({...newProduct, mas_info_pc: e.target.value})} />
            </div>
            <div className="col-span-2 border-t pt-3 mt-1">
              <Label className="text-sm font-medium mb-2 block">{tx('Temáticas sensibles', 'Sensitive topics')}</Label>
              <div className="flex flex-wrap gap-3">
                {['casino', 'cbd', 'gambling', 'cryptocurrency', 'sex', 'erotic', 'vpn', 'tarot', 'cannabis'].map(topic => (
                  <div key={topic} className="flex items-center gap-1.5">
                    <Checkbox
                      id={`add-${topic}`}
                      checked={newProduct[topic]}
                      onCheckedChange={(v) => setNewProduct({...newProduct, [topic]: !!v})}
                      className="h-3.5 w-3.5"
                    />
                    <Label htmlFor={`add-${topic}`} className="text-xs cursor-pointer capitalize">{topic}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProduct(false)}>{tx('Cancelar', 'Cancel')}</Button>
            <Button onClick={handleAddProduct} disabled={addingProduct} className="bg-green-600 hover:bg-green-700">
              {addingProduct ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {tx('Crear Producto', 'Create Product')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsTab;
